import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import authApi from '../../api/authApi';

const initialState = {
  isLogin: false,
  user: null,
  isAuthChecked: false,
  isLoading: false,
  error: null,
};

export const fetchCurrentAdmin = createAsyncThunk('auth/fetchCurrentAdmin', async (_, thunkAPI) => {
  try {
    const response = await authApi.getMeAdmin();
    const body = response?.body;

    // Backend may return { success, data } or legacy shapes; session is invalid if explicitly false.
    if (body && body.success === false) {
      return thunkAPI.rejectWithValue(body?.message || 'Not authenticated');
    }

    const user =
      body?.data ?? (body && typeof body === 'object' && body._id && !body.success ? body : null);

    if (!user) {
      return thunkAPI.rejectWithValue('Unable to load current admin session');
    }

    return user;
  } catch (error) {
    return thunkAPI.rejectWithValue(
      error?.response?.data?.message || 'Unable to fetch current admin',
    );
  }
});

export const logoutAdminSession = createAsyncThunk('auth/logoutAdminSession', async () => {
  try {
    await authApi.logoutAdmin();
  } catch (error) {
    // Always clear client auth state even if server logout fails.
  }
  localStorage.removeItem('user');
  localStorage.removeItem('isLogin');
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess(state, action) {
      state.isLogin = true;
      state.user = action.payload || null;
      state.isAuthChecked = true;
      state.error = null;
    },
    clearAuthState(state) {
      state.isLogin = false;
      state.user = null;
      state.isAuthChecked = true;
      state.error = null;
    },
    updateUser(state, action) {
      state.user = action.payload || state.user;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentAdmin.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCurrentAdmin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLogin = true;
        state.user = action.payload || null;
        state.isAuthChecked = true;
        state.error = null;
      })
      .addCase(fetchCurrentAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.isLogin = false;
        state.user = null;
        state.isAuthChecked = true;
        state.error = action.payload || action.error?.message || null;
      })
      .addCase(logoutAdminSession.fulfilled, (state) => {
        state.isLogin = false;
        state.user = null;
        state.isAuthChecked = true;
        state.error = null;
      });
  },
});

export const { loginSuccess, clearAuthState, updateUser } = authSlice.actions;

export default authSlice.reducer;
